import * as React from 'react';
import {
  Box, IconButton, LinearProgress, Menu, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';

import Row from './Row';
import { sortRowsByColumn } from 'utils';
import { ExportList } from './FilterList';
import TableNoRowsOverlay from 'components/TableNoRowsOverlay';
import { getListActions } from 'components/DynamicTable';
import { DebouncedTextField } from 'components/CustomFields';
import CustomButton from 'components/CustomButtons';

const sx = {
  tableCell: {
    whiteSpace: 'nowrap',
    backgroundColor: '#F1F5FE',
    color: '#40729D',
    borderBottom: '2px solid #C1C2C2',
    borderTop: '2px solid #C1C2C2',
  },
  tableHeader: {
    whiteSpace: 'nowrap',
  },
  stickyColumn: {
    position: "sticky",
    zIndex: 10,
    padding: 0,
    paddingLeft: '5px'
  },
  actionColumn: {
    whiteSpace: 'nowrap',
    position: "sticky",
    right: '-1px',
    zIndex: 10,
    display: 'flex-end',
    width: '40px',
  },
  selectButton: {
    fontSize: '12px',
    textTransform: 'capitalize',
    color: "#000",
    whiteSpace: 'nowrap',
    paddingLeft: '5px'
  },
  sortIcon: {
    fontSize: '15px',
    cursor: 'pointer',
    display: 'flex',
    color: '#135EBF'
  },
  iconBtn: {
    color: '#fff',
    '&: hover': {
      backgroundColor: '#000'
    }
  },
  searchIcon: {
    position: 'absolute',
    left: 0,
    color: '#464545',
    padding: '2px'
  },
  sortByButton: {
    fontSize: '12px',
    textTransform: 'capitalize',
    border: "2px solid #02569D",
    whiteSpace: 'nowrap',
    color: "#02569D",
  },
  emptyDialogBox: {
    width: "100%",
    minHeight: '200px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '30px 0px',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center', height: '300px'
  }
};

const useStyles = makeStyles((theme) => ({
  iconButtonRoot: {
    color: '#fff',
    '&:hover': {
      backgroundColor: "#a8a3a347"
    },
  },
  headerRow: {
    height: '40px',
    '& td': {
      whiteSpace: 'nowrap',
      padding: theme.spacing(2.5),
    }
  },
  colContent: {
    display: 'flex', 
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  tableCell: {
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.neutral['clr-100'],
  },
}));

// Category button component (moved from FilterList)
const CategoryButton = ({ columns, filters, setFilters }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilters = (e) => {
    if (filters.includes(e.target.name)) {
      setFilters(filters.filter((f) => f !== e.target.name));
    } else {
      setFilters([...filters, e.target.name]);
    }
  };

  return (
    <Box position='relative'>
      <CustomButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        size='medium'
        onClick={handleClick}
        variant='outlined'
      >
          Category: Multiple
        <ArrowDropDownIcon fontSize="14px"/>
      </CustomButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Box id='categories'>
          <Box display='flex' flexDirection='column' gap='4px' px='6px' fontSize='12px'>
            {columns.map((col, index) => (
              <label key={index} htmlFor={`filter${col.field}`} style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 2}}>
                <input
                  checked={filters.includes(col.field)}
                  onChange={handleFilters}
                  disabled={col.fixed}
                  type='checkbox'
                  id={`filter${col.field}`}
                  name={col.field} 
                />
                <Typography variant='body02-semiBold'>
                  {col.label}
                </Typography>
              </label>
            ))}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

// sort arrow component
const SortList = ({ column, rows, setRowData }) => {
  const classes = useStyles();
  const [sortOrder, setSortOrder] = React.useState(false);

  const handleSorting = () => {
    if (!rows) return
    setSortOrder(!sortOrder);
    const sorted = sortRowsByColumn(rows, column, sortOrder ? "desc" : 'asc')
    setRowData([...sorted])
  }

  React.useEffect(() => {
    if (column?.sort && rows.length > 1) {
      handleSorting()
    }
    else {
      setSortOrder([...rows])
    }
  }, [column, rows])

  return (
    <>
      {
        sortOrder
          ?
          <IconButton size='small' classes={{ root: classes.iconButtonRoot }}>
            <ArrowDownwardIcon onClick={handleSorting} sx={sx.sortIcon} />
          </IconButton>
          :
          <IconButton size='small' classes={{ root: classes.iconButtonRoot }}>
            <ArrowUpwardIcon onClick={handleSorting} sx={sx.sortIcon} />
          </IconButton>
      }
    </>
  )
}

const StaticTable = ({
  width, height='fit-content', columns, actions, rows, exportRows, forTemplateAnalytics , initialFilters = [], 
  noRowsMessage, exportByAttempts = false, showRowSelectCheckbox = false,
  setRowSelectionModel = () => { }, rowSelectionModel = [], loading,
  showColumnSelection = true, showExports = true,
  useItemProps, tableHeaderClasses, tableBodyClasses,
  ...props
}) => {
  const classes = useStyles();

  const [filters, setFilters] = React.useState(initialFilters || []);
  const [exportFilters, setExportFilters] = React.useState(initialFilters || []);
  const [rowData, setRowData] = React.useState(rows || []);
  const [search, setSearch] = React.useState('');
  const [hasInteracted, setHasInteracted] = React.useState(!!(props?.outerSearchString || ''));

  React.useEffect(() => {
    const updatedRows = rows.map(row => 
      row.type === 'personalized' ? { ...row, type: 'mock interview' } : row
    );
    setRowData(updatedRows);
  }, [rows]);
  
  const handleRowSelection = (data) => {
    if (rowSelectionModel?.includes(data._id)) {
      setRowSelectionModel(rowSelectionModel.filter(r => r !== data._id));
    }
    else {
      setRowSelectionModel([...rowSelectionModel, data._id]);
    }
  }

  const handleSelectAll = () => {
    if (rowSelectionModel.length === rowData.length) {
      setRowSelectionModel([]);
    } else {
      const allRowIds = rowData.map((row) => row._id);
      setRowSelectionModel(allRowIds);
    }
  };

  React.useEffect(() => {
    setFilters(initialFilters);
    setExportFilters(initialFilters);
  }, [initialFilters.length])

  const filteredColumns = React.useMemo(() => {
    let allColumns = columns.filter((col) => {
      if (filters.length) {
        return filters.includes(col.field)
      }
      else {
        return col;
      }
    })

    if (showRowSelectCheckbox) {
      allColumns.unshift({
        field: 'select',
        label: (
          <input type='checkbox' name='selectAll'
            checked={rowData.length && rowSelectionModel.length === rowData.length}
            onChange={handleSelectAll}
          />
        ),
        type: 'select',
        align: 'start',
        disableSorting: true,
        minWidth: '20px',
        maxWidth: '20px',
        getActions: (params) => getListActions({
          params, rowSelection: true, rowSelectionModel
        })
      })
    }

    if (actions) {
      allColumns.push({
        field: 'actions',
        label: 'Actions',
        type: "actions",
        align: 'center',
        disableSorting: true,
        minWidth: 100,
        getActions: (params) => getListActions({
          params, customActions: actions,
        }),
      });
    }

    return allColumns;
  }, [filters, columns, actions, rowSelectionModel]);

  React.useEffect(() => {
    // Always use rows from parent; do not locally filter on keystrokes
    setRowData(rows || []);
  }, [rows])

  const handleDeselectFilter = (field) => {
    if (filters.includes(field)) {
      setFilters(filters.filter((data) => data !== field));
    }
  }

  return (
    <Box style={{ width: width || '100%' }}>
      {/* search */}
      {
        showExports ?
          <Box display='flex' justifyContent='space-between' mb='16px' alignItems='center'>
            <Box display='flex' gap={1} alignItems='center'>
              <DebouncedTextField
                placeholder="Search..."
                defaultValue={search}
                onChange={(value) => { setSearch(value); if (!hasInteracted) setHasInteracted(true); }}
                width={350}
              />
              <CustomButton
                variant="contained"
                size="medium"
                disabled={!hasInteracted && (!search || !search.trim())}
                onClick={() => props?.onSearch?.(search.trim())}
              >
                Search
              </CustomButton>
            </Box>
            <Box display='flex' gap='10px'>
              <CategoryButton 
                columns={columns}
                filters={filters}
                setFilters={setFilters}
              />
              <ExportList
                columns={columns}
                rows={rows}
                exportRows={exportRows}
                filters={exportFilters}
                setFilters={setExportFilters}
                exportByAttempts={exportByAttempts}
                useItemProps={useItemProps}
                {...props}
                forTemplateAnalytics={forTemplateAnalytics}
                userId={props?.userId}
                selectedBatch={props?.selectedBatch}
              />
            </Box>
          </Box>
          : null
      }
      {/* filters - using the existing FilterList logic */}
      {
        showColumnSelection ?
          <Box display='flex' flexDirection='column' mb='16px' alignItems='flex-start' width='100%'>
            <Box display='flex' alignItems='center' width='100%'>
              <Typography variant="body01-bold" mr='16px'>Applied filters:</Typography>
              <Box display='flex' gap='12px' alignItems='center' flexWrap='wrap' style={{overflowX: 'auto', maxWidth: '70vw'}}>
                {columns?.map((col, index) => (
                  filters.includes(col?.field) && !col.fixed ? (
                    <Box key={index} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      fontSize: '13px',
                      backgroundColor: '#F5F9FF',
                      padding: '5px 8px',
                      borderRadius: '12px',
                      color: '#464545'
                    }}>
                      <Typography variant="body01-semiBold" color='neutral.clr-700'>
                        {col?.label}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          display: 'flex',
                          fontSize: '20px',
                          alignItems: 'center',
                          cursor: 'pointer',
                          p: 0,
                          minWidth: 'auto'
                        }}
                        onClick={() => handleDeselectFilter(col.field)}
                      >
                        <CloseIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Box>
                  ) : null
                ))}
              </Box>
            </Box>
          </Box>
          : null
      }

      {/* table */}
      {
        <Box style={{ visibility: loading ? 'visible' : 'hidden', padding: '0px 0.5px' }}>
          <LinearProgress />
        </Box>
      }
      <TableContainer sx={{ border: '1px solid #E1E2E3', height }}>
        <Table stickyHeader size="small" aria-label="a dense table">
          <TableHead>
            <TableRow className={classes.headerRow}>
              {
                filteredColumns?.map((col, index) => {
                  return (
                    <>
                      <TableCell
                        className={classes.tableCell}
                        align={col?.align || 'center'}
                        key={index}
                        sx={
                          col?.type === 'actions'
                            ? sx.actionColumn
                            : index === 0
                              ? { ...(sx.stickyColumn), left: 0 }
                              : (index === 1 && showRowSelectCheckbox)
                                ? { ...(sx.stickyColumn), left: '25px' } : null
                        }
                        style={{
                          minWidth: col?.minWidth,
                          maxWidth: col?.maxWidth,
                        }}
                      >
                        <Box
                          className={classes.colContent}
                          justifyContent={col?.align || 'start'}
                        >
                          <Typography variant='body01-bold'>{col?.label}</Typography>
                          <span>
                            {!col?.disableSorting &&
                              <SortList column={col} rows={rows} setRowData={setRowData} />
                            }
                          </span>
                        </Box>
                      </TableCell>
                    </>
                  )
                })
              }
            </TableRow>
          </TableHead>
          {
            rowData.length ?
              <TableBody className={tableBodyClasses}>
                {
                  rowData.map((item, index) => (
                    <Row
                      key={item.id}
                      rowData={item}
                      rowNo={index}
                      columns={filteredColumns}
                      getActions={actions}
                      handleRowSelection={handleRowSelection}
                      showRowSelectCheckbox={showRowSelectCheckbox}
                      rowSelectionModel={rowSelectionModel}
                    />
                  ))
                }
              </TableBody>
              :
              null
          }
        </Table>
        {
          rowData.length ? null :
            <Box sx={sx.emptyDialogBox} >
              <TableNoRowsOverlay message={noRowsMessage} />
            </Box>
        }
      </TableContainer>
    </Box>
  )
}

export default StaticTable;